/**
 * Page-local copy and ids for /free-import-advice.
 *
 * Nothing here is a new business claim, and that is a constraint rather than
 * an observation. The three proof points, the free-and-no-obligation line and
 * the description of what a visitor should send are all restatements of copy
 * Kabex has already approved — the `import-consultation` entry in
 * `content/services.ts` (§59) and §32's "a request, not a confirmed booking"
 * (A16). A page that exists to convert is the last place on the site that
 * should be inventing a promise.
 *
 * The request's own words are not here either: the form's title, the four
 * steps and the WhatsApp message are in `content/consultation.ts`, shared with
 * the band on /contact. What lives here is the furniture around them — the ids
 * the bands are labelled by, the headings, and the two sentences that point a
 * visitor at the pages this one deliberately does not duplicate.
 */

import { type Crumb, type PageIntroHighlight } from '@/components/molecules';
import { CONSULTATION_WHATSAPP_MESSAGE } from '@/content/consultation';
import { servicePathForTitle } from '@/content/services';

/** The route's WhatsApp message is the shared one; the page states it once. */
export { CONSULTATION_WHATSAPP_MESSAGE as WHATSAPP_MESSAGE } from '@/content/consultation';

/** `Breadcrumbs` renders Home itself; the schema has to be told about it. */
export const crumbs: readonly Crumb[] = [{ label: 'Free Import Advice' }];

export const REQUEST_TITLE_ID = 'import-advice-request-title';
export const WAYS_TITLE_ID = 'import-advice-ways-title';

/**
 * The anchor the intro's one action points at, and the form's own band.
 *
 * Exported because two files need it and neither should retype it. Anything a
 * `#id` points at needs `anchorOffsetSx` or the sticky header lands on top of
 * it — see `PageIntroActions`.
 */
export const REQUEST_ID = 'ask';

/**
 * Three proof points, each answering one of the three objections that stop a
 * visitor asking: what it will cost, whether asking commits them, and how long
 * they will be waiting.
 *
 * The first is Kabex's existing wording, from the `facts` on the
 * import-consultation service page. The other two restate §32 and §28.
 */
export const introHighlights: readonly PageIntroHighlight[] = [
  { icon: 'price', label: 'Free, and not conditional on buying' },
  { icon: 'shield', label: 'No deposit, nothing reserved' },
  { icon: 'chat', label: 'Answered on WhatsApp by our team' },
];

export const intro = {
  eyebrow: 'Free import advice',
  title: 'Ask before you buy, not after it lands',
  lede: 'Most expensive import mistakes are made before any money moves. Tell us what you are considering and someone who imports for a living will tell you what we would do — including when the answer is "not that car".',
  action: 'Ask our team',
} as const;

export const request = {
  eyebrow: 'Your question',
  title: 'Tell us what you are considering',
  lede: 'A model, a shortlist, or just a budget and what you need the car to do. All three are a usable starting point.',
} as const;

export const assurance = {
  talkNowTitle: 'Rather talk now?',
} as const;

export const ways = {
  eyebrow: 'How we answer',
  title: 'Four ways to get the answer',
  lede: 'Whichever you choose, the same team answers. Pick the one that suits you in the form above — you are not locked into it.',
} as const;

/**
 * The link out to the page that explains what the advice covers.
 *
 * This page asks the question; `/services/import-consultation` is the page
 * that argues for asking it, with the six inclusions and the three questions
 * people actually ask. Linking rather than restating is the whole point: two
 * pages carrying the same six bullet points and the same FAQ would be two
 * pages competing for the same search, and the one without the form would keep
 * winning it.
 */
export const scopeLink = {
  title: 'What we actually go through',
  text: 'The six things the advice covers, what the age limit and the environmental levy mean for your year, and whether we can advise on a car you have already bought.',
  label: 'What import consultation covers',
  to: servicePathForTitle('Import consultation'),
} as const;

export const closing = {
  title: 'Already know the car you want?',
  text: 'Skip the questions and ask for a quotation instead — the car, shipping, URA taxes and clearing, each as its own line. You can always come back for advice.',
  whatsappMessage: CONSULTATION_WHATSAPP_MESSAGE,
} as const;
